import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { categoryAPI, notificationAPI, insightAPI, taskAPI } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Bell, User, Lock, Unlock, ArrowRight, Sparkles, Lightbulb, Check, ChevronLeft, ChevronRight, Trophy, Flame, Calendar as CalendarIcon, BarChart3, MessageSquarePlus, HelpCircle, Send, Trash2, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { AMAZON_LINK } from '@/components/AffiliateFooter';

const CATEGORY_ICONS = {
  debloat: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/559174ef494ad1f0666c93a97b367bb3647d81faada2ae730e8921b094fea138.png',
  asymmetry: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/4fc82b76eacba289812d144b8a1a9e41fa8f1e86dd725870f329edf842b8c96e.png',
  coloring: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/0eeee50ce3b71916b765aa9683f50382298e9949d173ae904cee8ba8255a50f3.png',
  ratios: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/05fa82b4d2d73397be7293bcae5ca94148f4293ff3e948d271f96a2018cd77b8.png',
  physique: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/461b1f58ca8deed220fb566f1cac62d2ba1317cd1cbdf44cc5be718d42b0262b.png',
  styling: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/47c66145bf2de875fe849e83474cb7c7f17c98ae43c71830465625366c691712.png',
  status: 'https://static.prod-images.emergentagent.com/jobs/379f3f68-078c-4206-97bd-0243b65c1e8e/images/3ed3e498d5f3c557b427ade43b540eacdd35e4331b3ec32d9be27edc9b899ffd.png',
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function DashboardPage() {
  const [categories, setCategories] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [insights, setInsights] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [weekScore, setWeekScore] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('today');
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadTasks(selectedDate); loadNotes(selectedDate); }, [selectedDate]);

  const loadData = async () => {
    try {
      const [catRes, notifRes, insightRes, scoreRes] = await Promise.all([
        categoryAPI.getAll(),
        notificationAPI.getAll(),
        insightAPI.getAll(),
        taskAPI.getWeekScore(),
      ]);
      setCategories(catRes.data.categories || []);
      setUnreadCount(notifRes.data.unread_count || 0);
      setInsights(insightRes.data.insights || []);
      setWeekScore(scoreRes.data);
      // Load monthly summary
      taskAPI.getMonthlySummary().then(res => setMonthlySummary(res.data)).catch(() => {});
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async (date) => {
    try {
      const isToday = date === new Date().toISOString().split('T')[0];
      const res = isToday ? await taskAPI.getToday() : await taskAPI.getCalendar(date);
      setTasks(res.data.tasks || []);
    } catch { /* ignore */ }
  };

  const loadNotes = async (date) => {
    try {
      const res = await taskAPI.getNotes(date);
      setNotes(res.data.notes || []);
    } catch { /* ignore */ }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await taskAPI.addNote({ note: noteText, date: selectedDate });
      setNoteText('');
      await loadNotes(selectedDate);
      toast.success('Note added! Tasks will consider this context.');
    } catch {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await taskAPI.deleteNote(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch { /* ignore */ }
  };

  const handleToggleTask = async (taskId) => {
    try {
      const res = await taskAPI.toggle(taskId, selectedDate);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, completed: res.data.completed } : t));
      const scoreRes = await taskAPI.getWeekScore();
      setWeekScore(scoreRes.data);
      if (res.data.completed) {
        toast.success('Task completed!', { duration: 1500 });
      }
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleToggleLock = async (slug, e) => {
    e.stopPropagation();
    try {
      const res = await categoryAPI.toggleLock(slug);
      setCategories(prev => prev.map(c => c.slug === slug ? { ...c, locked: res.data.locked } : c));
      toast.success(res.data.locked ? 'Category paused' : 'Category resumed');
    } catch {
      toast.error('Failed');
    }
  };

  const navigateDate = (offset) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    const newDate = d.toISOString().split('T')[0];
    // Don't allow navigating past today
    if (newDate > today) return;
    setSelectedDate(newDate);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 bg-purple-200 rounded-xl flex items-center justify-center animate-pulse">
          <span className="text-white font-bold text-xl font-['Manrope']">B</span>
        </div>
      </div>
    );
  }

  const today = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === today;
  const completedTasks = tasks.filter(t => t.completed).length;
  const totalTasks = tasks.length;
  const dayPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const hasIncompleteCategories = categories.some(c => !c.data);

  const tasksByCategory = {};
  tasks.forEach(t => {
    if (!tasksByCategory[t.category]) tasksByCategory[t.category] = [];
    tasksByCategory[t.category].push(t);
  });

  const getWeekDays = () => {
    const todayDate = new Date();
    const startOfWeek = new Date(todayDate);
    startOfWeek.setDate(todayDate.getDate() - todayDate.getDay() + 1);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return {
        date: d.toISOString().split('T')[0],
        label: DAY_NAMES[i],
        isToday: d.toISOString().split('T')[0] === todayDate.toISOString().split('T')[0],
        score: weekScore?.daily_scores?.find(s => s.date === d.toISOString().split('T')[0]),
      };
    });
  };

  return (
    <TooltipProvider delayDuration={0}>
    <div className="min-h-screen bg-white pb-28" data-testid="dashboard-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-lg mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-purple-200 rounded-xl flex items-center justify-center shadow-inner">
              <span className="text-white font-bold text-lg font-['Manrope']">B</span>
            </div>
            <span className="text-lg font-semibold tracking-tight font-['Manrope']">Brand OS</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="notifications-btn" onClick={() => navigate('/notifications')} className="relative w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 hover:bg-gray-100" style={{ transition: 'background-color 0.2s' }}>
              <Bell className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
              {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 brand-gradient rounded-full flex items-center justify-center text-white text-[10px] font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>}
            </button>
            <button data-testid="profile-btn" onClick={() => navigate('/profile')} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 hover:bg-gray-100" style={{ transition: 'background-color 0.2s' }}>
              <User className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Greeting */}
        <div className="mb-6 animate-fade-in-up">
          <p className="text-sm font-medium text-gray-400 uppercase tracking-widest mb-1">Dashboard</p>
          <h1 className="text-3xl font-bold tracking-tight">Hey, {user?.name?.split(' ')[0] || 'there'}</h1>
        </div>

        {/* Weekly Score Card */}
        {weekScore && weekScore.total_possible > 0 && (
          <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
            <div className="brand-gradient rounded-2xl p-[1px]">
              <div className="bg-white rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
                    <span className="text-sm font-semibold">Weekly Score</span>
                  </div>
                  <span className="text-2xl font-bold brand-gradient-text" data-testid="week-score">{weekScore.score}%</span>
                </div>
                <div className="flex gap-1.5 justify-between">
                  {getWeekDays().map(day => {
                    const pct = day.score ? (day.score.total > 0 ? (day.score.completed / day.score.total) * 100 : 0) : 0;
                    return (
                      <button
                        key={day.date}
                        data-testid={`week-day-${day.date}`}
                        onClick={() => setSelectedDate(day.date)}
                        className={`flex flex-col items-center gap-1 px-2 py-1.5 rounded-xl flex-1 ${selectedDate === day.date ? 'bg-gray-100' : ''} ${day.isToday ? 'ring-1 ring-purple-200' : ''}`}
                        style={{ transition: 'background-color 0.2s' }}
                      >
                        <span className="text-[10px] text-gray-400 font-medium">{day.label}</span>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          pct >= 100 ? 'bg-green-500 text-white' :
                          pct > 0 ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-400'
                        }`}>
                          {pct >= 100 ? <Check className="w-3 h-3" strokeWidth={2.5} /> : day.isToday ? <Flame className="w-3 h-3" /> : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Incomplete Categories Banner */}
        {hasIncompleteCategories && (
          <div className="mb-6 p-4 bg-purple-50 rounded-2xl border border-purple-100 animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-purple-600" strokeWidth={1.5} />
              <span className="text-sm font-semibold text-purple-700">Setup incomplete</span>
            </div>
            <p className="text-xs text-purple-500">Some categories need your info. Fill them in to unlock daily tasks.</p>
          </div>
        )}

        {/* Tab Switcher */}
        <div className="flex gap-1 bg-gray-50 rounded-xl p-1 mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <button
            data-testid="tab-today"
            onClick={() => setTab('today')}
            className={`flex-1 h-10 rounded-lg text-sm font-medium ${tab === 'today' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
            style={{ transition: 'all 0.2s' }}
          >
            <CalendarIcon className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
            Daily Tasks
          </button>
          <button
            data-testid="tab-categories"
            onClick={() => setTab('categories')}
            className={`flex-1 h-10 rounded-lg text-sm font-medium ${tab === 'categories' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
            style={{ transition: 'all 0.2s' }}
          >
            Categories
          </button>
          <button
            data-testid="tab-monthly"
            onClick={() => setTab('monthly')}
            className={`flex-1 h-10 rounded-lg text-sm font-medium ${tab === 'monthly' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
            style={{ transition: 'all 0.2s' }}
          >
            <BarChart3 className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
            Monthly
          </button>
        </div>

        {/* DAILY TASKS TAB */}
        {tab === 'today' && (
          <div className="animate-fade-in">
            {/* Date Navigator */}
            <div className="flex items-center justify-between mb-5">
              <button onClick={() => navigateDate(-1)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 hover:bg-gray-100" data-testid="date-prev">
                <ChevronLeft className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
              </button>
              <div className="text-center">
                <p className="text-sm font-semibold">{isToday ? 'Today' : new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' })}</p>
                <p className="text-xs text-gray-400">{new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
              </div>
              <button
                onClick={() => navigateDate(1)}
                disabled={selectedDate >= today}
                className={`w-8 h-8 flex items-center justify-center rounded-full ${selectedDate >= today ? 'bg-gray-50 opacity-30 cursor-not-allowed' : 'bg-gray-50 hover:bg-gray-100'}`}
                data-testid="date-next"
              >
                {selectedDate >= today ? (
                  <Lock className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.5} />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
                )}
              </button>
            </div>

            {/* Day Progress */}
            {totalTasks > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">{completedTasks} of {totalTasks} tasks</span>
                  <span className="text-sm font-bold" data-testid="day-progress-pct">{dayPct}%</span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${dayPct >= 100 ? 'bg-green-500' : dayPct > 50 ? 'brand-gradient' : 'bg-purple-300'}`}
                    style={{ width: `${dayPct}%`, transition: 'width 0.6s ease-out' }}
                  />
                </div>
                {dayPct >= 100 && (
                  <div className="flex items-center gap-2 mt-2 text-green-600">
                    <Trophy className="w-4 h-4" strokeWidth={1.5} />
                    <span className="text-sm font-semibold">All tasks complete! Great work.</span>
                  </div>
                )}
              </div>
            )}

            {/* Task Groups */}
            {totalTasks === 0 ? (
              <div className="bg-gray-50 rounded-2xl p-8 text-center">
                <CalendarIcon className="w-8 h-8 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-gray-400 mb-1">No tasks for this day</p>
                <p className="text-xs text-gray-300">Fill in your categories to generate daily tasks</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(tasksByCategory).map(([catSlug, catTasks]) => {
                  const catInfo = categories.find(c => c.slug === catSlug);
                  return (
                    <div key={catSlug}>
                      <div className="flex items-center gap-2 mb-3">
                        <img src={CATEGORY_ICONS[catSlug]} alt={catSlug} className="w-6 h-6 rounded object-contain" loading="lazy" />
                        <span className="text-sm font-semibold capitalize">{catInfo?.name || catSlug}</span>
                        <span className="text-xs text-gray-400 ml-auto">{catTasks.filter(t => t.completed).length}/{catTasks.length}</span>
                      </div>
                      <div className="space-y-2">
                        {catTasks.map(task => (
                          <div
                            key={task.id}
                            data-testid={`task-${task.id}`}
                            className={`w-full rounded-xl border text-left ${
                              task.completed
                                ? 'bg-green-50/50 border-green-100'
                                : 'bg-white border-gray-100 hover:border-purple-200'
                            }`}
                            style={{ transition: 'all 0.2s' }}
                          >
                            <button
                              onClick={() => handleToggleTask(task.id)}
                              className="w-full flex items-start gap-3 p-4"
                            >
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                task.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                              }`} style={{ transition: 'all 0.2s' }}>
                                {task.completed && <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${task.completed ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                                  {task.title}
                                </p>
                                {task.description && (
                                  <p className="text-xs text-gray-400 mt-0.5 whitespace-pre-wrap">{task.description}</p>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                  task.time_of_day === 'morning' ? 'bg-amber-50 text-amber-600' :
                                  task.time_of_day === 'evening' ? 'bg-indigo-50 text-indigo-600' :
                                  'bg-gray-50 text-gray-400'
                                }`}>{task.time_of_day}</span>
                                <span className="text-[9px] text-gray-300">{task.frequency}</span>
                              </div>
                            </button>
                            {task.has_product && task.product_name && (
                              <div className="px-4 pb-3 pt-0">
                                <a
                                  href={AMAZON_LINK}
                                  target="_blank"
                                  rel="noopener noreferrer nofollow"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF9900] text-white text-[11px] font-semibold rounded-lg hover:bg-[#e88b00]"
                                  style={{ transition: 'background-color 0.2s' }}
                                  data-testid={`amazon-btn-${task.id}`}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <ExternalLink className="w-3 h-3" strokeWidth={2} />
                                  Check Price on Amazon
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Insights */}
            {insights.length > 0 && (
              <div className="mt-8">
                <div className="bg-purple-50 rounded-2xl p-5 border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-purple-600" strokeWidth={1.5} />
                    <span className="text-sm font-semibold text-purple-700">Insight</span>
                  </div>
                  <p className="text-sm text-purple-600/80 leading-relaxed" data-testid="insight-text">{insights[0]?.description}</p>
                </div>
              </div>
            )}

            {/* Notes Section */}
            <div className="mt-8" data-testid="notes-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MessageSquarePlus className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">Notes</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="p-0.5 text-gray-300 hover:text-purple-500 transition-colors" data-testid="notes-info-tooltip">
                        <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[260px] bg-[#09090b] text-white text-xs p-3 rounded-xl border-0 shadow-xl">
                      <p>Add notes about your day (parties, sick days, travel, etc.) and the AI will consider them when suggesting tasks for today and future days.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className="text-xs text-purple-600 font-medium hover:text-purple-700"
                  data-testid="toggle-notes-btn"
                  style={{ transition: 'color 0.2s' }}
                >
                  {showNotes ? 'Hide' : notes.length > 0 ? `Show (${notes.length})` : 'Add Note'}
                </button>
              </div>

              {showNotes && (
                <div className="space-y-3 animate-fade-in">
                  {/* Note Input */}
                  <div className="flex gap-2">
                    <Textarea
                      data-testid="note-input"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="e.g., 'Have a party tonight, won't be dieting this evening'"
                      className="min-h-[60px] bg-gray-50 border-transparent rounded-xl p-3 text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:bg-white placeholder:text-gray-400 flex-1"
                      style={{ transition: 'background-color 0.2s, box-shadow 0.2s' }}
                    />
                    <button
                      data-testid="submit-note-btn"
                      onClick={handleAddNote}
                      disabled={addingNote || !noteText.trim()}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-50 text-purple-600 hover:bg-purple-100 disabled:opacity-40 self-end"
                      style={{ transition: 'background-color 0.2s' }}
                    >
                      <Send className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>

                  {/* Existing Notes */}
                  {notes.map(n => (
                    <div key={n.id} className="flex items-start gap-2 bg-gray-50 rounded-xl p-3" data-testid={`note-${n.id}`}>
                      <p className="text-sm text-gray-600 flex-1 leading-relaxed">{n.note}</p>
                      <button onClick={() => handleDeleteNote(n.id)} className="p-1 text-gray-300 hover:text-red-400 flex-shrink-0" style={{ transition: 'color 0.2s' }}>
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* CATEGORIES TAB */}
        {tab === 'categories' && (
          <div className="space-y-3 animate-fade-in stagger-children">
            {categories.map((cat) => (
              <div
                key={cat.slug}
                data-testid={`category-card-${cat.slug}`}
                className="group flex items-center gap-4 p-4 rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] cursor-pointer"
                style={{ transition: 'box-shadow 0.3s' }}
                onClick={() => navigate(`/category/${cat.slug}`)}
              >
                <img
                  src={CATEGORY_ICONS[cat.slug]}
                  alt={cat.name}
                  className="w-12 h-12 rounded-xl object-contain bg-gray-50 p-1"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="font-semibold tracking-tight text-sm">{cat.name}</h3>
                    {cat.data ? (
                      <span className="text-[10px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">Active</span>
                    ) : (
                      <span className="text-[10px] font-medium text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-full">Setup</span>
                    )}
                    {cat.locked && <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">Paused</span>}
                  </div>
                  <p className="text-xs text-gray-400 line-clamp-1">{cat.description}</p>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <Switch
                    data-testid={`lock-toggle-${cat.slug}`}
                    checked={!cat.locked}
                    onCheckedChange={() => handleToggleLock(cat.slug, { stopPropagation: () => {} })}
                    className="scale-75"
                  />
                  {cat.locked ? <Lock className="w-3.5 h-3.5 text-gray-300" strokeWidth={1.5} /> : <Unlock className="w-3.5 h-3.5 text-green-500" strokeWidth={1.5} />}
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-purple-500 flex-shrink-0" strokeWidth={1.5} />
              </div>
            ))}
          </div>
        )}

        {/* MONTHLY SUMMARY TAB */}
        {tab === 'monthly' && (
          <div className="animate-fade-in">
            {monthlySummary ? (
              <div className="space-y-6">
                {/* Overall Score */}
                <div className="text-center py-6">
                  <p className="text-sm text-gray-400 mb-2">{monthlySummary.month}</p>
                  <div className="text-5xl font-bold brand-gradient-text mb-2" data-testid="monthly-overall-score">
                    {monthlySummary.overall_score}%
                  </div>
                  <p className="text-sm text-gray-400">Overall Monthly Score</p>
                  <p className="text-xs text-gray-300 mt-1">{monthlySummary.days_tracked} days tracked</p>
                </div>

                {/* Per-Category Scores */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-600">Category Breakdown</h3>
                  {monthlySummary.category_scores?.map(cs => {
                    const catInfo = categories.find(c => c.slug === cs.category);
                    return (
                      <div key={cs.category} className="flex items-center gap-3 p-4 rounded-xl border border-gray-100" data-testid={`monthly-cat-${cs.category}`}>
                        <img src={CATEGORY_ICONS[cs.category]} alt={cs.category} className="w-8 h-8 rounded-lg object-contain bg-gray-50 p-0.5" loading="lazy" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium capitalize">{catInfo?.name || cs.category}</span>
                            <span className={`text-sm font-bold ${cs.score >= 80 ? 'text-green-600' : cs.score >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                              {cs.score}%
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${cs.score >= 80 ? 'bg-green-500' : cs.score >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                              style={{ width: `${cs.score}%`, transition: 'width 0.6s ease-out' }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-300 mt-1">{cs.completed}/{cs.total} tasks done</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Monthly Insight */}
                {monthlySummary.overall_score >= 80 && (
                  <div className="bg-green-50 rounded-2xl p-5 border border-green-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                      <span className="text-sm font-bold text-green-700">Excellent Month!</span>
                    </div>
                    <p className="text-sm text-green-600/80">You're crushing it! Keep up this consistency and you'll see serious results.</p>
                  </div>
                )}
                {monthlySummary.overall_score < 50 && monthlySummary.overall_score > 0 && (
                  <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Flame className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                      <span className="text-sm font-bold text-amber-700">Room to Grow</span>
                    </div>
                    <p className="text-sm text-amber-600/80">Focus on completing your daily tasks consistently. Even small daily wins compound into big results.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-8 text-center">
                <BarChart3 className="w-8 h-8 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-gray-400">No monthly data yet</p>
                <p className="text-xs text-gray-300">Complete tasks to see your monthly summary</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <div className="fixed bottom-6 left-6 right-6 max-w-lg mx-auto h-16 bg-white/90 backdrop-blur-xl rounded-full shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-white/20 flex items-center justify-around z-50" data-testid="bottom-nav">
        <button data-testid="nav-dashboard" className="flex flex-col items-center gap-1 text-purple-600">
          <CalendarIcon className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-medium">Dashboard</span>
        </button>
        <button data-testid="nav-notifications" onClick={() => navigate('/notifications')} className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600" style={{ transition: 'color 0.2s' }}>
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-medium">Alerts</span>
        </button>
        <button data-testid="nav-profile" onClick={() => navigate('/profile')} className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600" style={{ transition: 'color 0.2s' }}>
          <User className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-[10px] font-medium">Profile</span>
        </button>
      </div>
    </div>
    </TooltipProvider>
  );
}
